import React, { useState, useCallback, useEffect, SetStateAction, Dispatch } from 'react';
import styled from '@emotion/styled';
import { Field, FormSection, RequiredAsterisk, FormHeader } from '@atlaskit/form';
import EditorInfoIcon from '@atlaskit/icon/glyph/editor/info';
import { token } from '@atlaskit/tokens';
import { P200 } from '@atlaskit/theme/colors';
import { Box } from '@atlaskit/primitives';
import { AddContactFields, AddContactMultiSelectField } from '@css-xp/ppc-add-contact-fields';
//eslint-disable-next-line
import { SelectCustomerAddress } from '@css-xp/ppc-select-customer-address';
import { getCurrencyByCountry, Currency } from '@css-xp/ppc-common';
import { CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';
import { getMaxLimitOfCustomerContact, isValidPrimaryContactDomain } from '@css-xp/ppc-utils';
import { BillingAdmin } from '@css-xp/ppc-graphtypes';
import { FormData } from '../../components/billing-profile-components/types';

const SoldToAddressFieldStyles = styled.div`
  margin-top: ${token('space.250')};
`;
const validatePrimaryEmailDomain = (email: string, validDomains: string[] | null | undefined): string | undefined => {
  const isPrimaryContactEmailValid = isValidPrimaryContactDomain({ email: email, partnerDomains: validDomains });
  if (!isPrimaryContactEmailValid) {
    return `Email address must not have the same domain as the partner account or its associated accounts`;
  }
  return undefined;
};

const CustomerContactSectionStyles = styled.div`
  > div {
    margin-top: ${token('space.250')};

    .add-contact-container {
      fieldset {
        margin-top: 0px;
        .contact-field {
          > div:nth-child(1) {
            > div:nth-child(1) {
              margin-top: 0px;
            }
          }
        }
      }
    }
  }
`;

interface CustomerDetailsStepProps {
  onFieldValueChange: (name: string, value: any) => void;
  onShipToAddressUpdate: (newAddress: CommerceAddress | null | undefined) => void;
  billingProfileData: FormData;
  customerAddresses?: CommerceAddress[];
  defaultSelectedAddress?: CommerceAddress | null | undefined;
  soldToPartiesLoading?: boolean;
  isMultiCurrencyChangesEnabled?: boolean;
  primaryCustomerContactAccountIds: string[];
  setPrimaryCustomerContactAccountIds: Dispatch<SetStateAction<Array<string>>>;
  primaryCustomerContactAccountEmails: string[];
  setPrimaryCustomerContactAccountEmails: Dispatch<SetStateAction<Array<string>>>;
  secondaryCustomerContactAccountIds: string[];
  setSecondaryCustomerContactAccountIds: Dispatch<SetStateAction<Array<string>>>;
  secondaryCustomerContactAccountEmails: string[];
  setSecondaryCustomerContactAccountEmails: Dispatch<SetStateAction<Array<string>>>;
  shouldHideFormHeader?: boolean;
  soldToAddressFieldLabel?: string;
  isSoldToAddressAdded?: boolean;
  handleAddNewAddressClick?: () => void;
  showAddNewAddressOption?: boolean;
  shouldRenderForInternalUse?: boolean;
  billingAdmins: Array<BillingAdmin>;
  setBillingAdmins: Dispatch<SetStateAction<Array<BillingAdmin>>>;
  txaBillingAdmins: Array<any>;
  billingAdminsLoading: boolean;
  setOtherContacts: Dispatch<SetStateAction<Array<BillingAdmin>>>;
  validDomains?: string[] | null | undefined;
  isCustomerContactsEditable?: boolean;
}

export const CustomerDetailsStep = ({
  onFieldValueChange,
  onShipToAddressUpdate,
  customerAddresses,
  billingProfileData,
  defaultSelectedAddress,
  soldToPartiesLoading,
  isMultiCurrencyChangesEnabled,
  primaryCustomerContactAccountIds,
  setPrimaryCustomerContactAccountIds,
  primaryCustomerContactAccountEmails,
  setPrimaryCustomerContactAccountEmails,
  secondaryCustomerContactAccountIds,
  setSecondaryCustomerContactAccountIds,
  secondaryCustomerContactAccountEmails,
  setSecondaryCustomerContactAccountEmails,
  shouldHideFormHeader,
  soldToAddressFieldLabel,
  isSoldToAddressAdded,
  handleAddNewAddressClick,
  showAddNewAddressOption,
  shouldRenderForInternalUse,
  billingAdmins,
  txaBillingAdmins,
  billingAdminsLoading,
  setBillingAdmins,
  setOtherContacts,
  validDomains,
  isCustomerContactsEditable,
}: CustomerDetailsStepProps) => {
  const [selectedSoldToAddress, setSelectedSoldToAddress] = useState(defaultSelectedAddress);
  const [isAddressSelected, setIsAddressSelected] = useState(false);
  const [showCurrencyWarning, setShowCurrencyWarning] = useState(false);

  useEffect(() => {
    if (isMultiCurrencyChangesEnabled && selectedSoldToAddress?.country) {
      const currency = getCurrencyByCountry(selectedSoldToAddress.country);
      setShowCurrencyWarning(currency !== Currency.USD);
    }
    onFieldValueChange('soldToAddress', selectedSoldToAddress);
  }, [onFieldValueChange, selectedSoldToAddress, showCurrencyWarning, isMultiCurrencyChangesEnabled]);

  const onSelect = useCallback(
    (selectedAddress: any) => {
      setSelectedSoldToAddress(selectedAddress);
      setIsAddressSelected(true);
      onShipToAddressUpdate(selectedAddress);
    },
    [onShipToAddressUpdate]
  );

  const maxLimitOfCustomerContact = getMaxLimitOfCustomerContact();
  const isAddNewSecondaryContactDisabled = secondaryCustomerContactAccountIds?.length >= maxLimitOfCustomerContact;

  const renderSoldToAddressField = () => {
    if (shouldRenderForInternalUse) {
      return (
        <SoldToAddressFieldStyles>
          <Field
            label={soldToAddressFieldLabel ?? 'Sold-to address'}
            name={'selectCustomerAddress'}
            id={'selectCustomerAddress'}
            isRequired={false}
          >
            {() => <div className="pt-8">Same as billing address</div>}
          </Field>
        </SoldToAddressFieldStyles>
      );
    }

    return (
      <SoldToAddressFieldStyles>
        <Field
          label={soldToAddressFieldLabel ?? 'Enter a sold-to address'}
          name={'selectCustomerAddress'}
          id={'selectCustomerAddress'}
          isRequired={true}
        >
          {({ fieldProps }) => (
            <SelectCustomerAddress
              onSelect={onSelect}
              soldToAddresses={customerAddresses}
              customClassName="customer-address-select"
              isLoading={soldToPartiesLoading}
              isAllSoldToAddressDisabled={true}
              fieldProps={fieldProps}
              defaultSoldToAddress={billingProfileData?.soldToAddress ?? undefined}
              handleAddNewAddressClick={handleAddNewAddressClick}
              showAddNewAddressOption={showAddNewAddressOption}
            />
          )}
        </Field>
      </SoldToAddressFieldStyles>
    );
  };

  const renderFormHeader = () => {
    if (shouldHideFormHeader) {
      return null;
    }

    if (shouldRenderForInternalUse) {
      return (
        <FormHeader>
          <h3>1. Details</h3>
        </FormHeader>
      );
    }

    return (
      <FormHeader>
        <h3>Add customer details</h3>
        Required fields are marked with an asterisk <RequiredAsterisk />
      </FormHeader>
    );
  };

  //@ts-ignore
  const existingBillingAdminIds = billingAdmins?.map(({ accountId }) => accountId);

  const renderPrimaryContactField = () => {
    if (shouldRenderForInternalUse) {
      const selectedKeyContactBillingAdmins = (billingProfileData as any)?.['primary-customer-contact-select'];

      return (
        <Field label="Key contact" name="primary-customer-contact" isRequired>
          {() => (
            <AddContactMultiSelectField
              currentUserList={existingBillingAdminIds}
              emailFieldText="Select from the list"
              fieldType="primary-customer-contact"
              billingAdmins={txaBillingAdmins.map((admin) => admin.node)}
              billingAdminsLoading={billingAdminsLoading}
              setBillingAdmins={setBillingAdmins}
              selectedUserList={selectedKeyContactBillingAdmins ?? []}
              isMulti={false}
            />
          )}
        </Field>
      );
    }

    return (
      <Field label="Enter a primary customer contact" name="primary-customer-contact" isRequired>
        {() => (
          <AddContactFields
            setAtlassianAccountIds={setPrimaryCustomerContactAccountIds}
            atlassianAccountIds={primaryCustomerContactAccountIds}
            atlassianAccountEmails={primaryCustomerContactAccountEmails}
            setAtlassianAccountEmails={setPrimaryCustomerContactAccountEmails}
            userAlreadyExistText="Customer contact already exists"
            emailFieldText="Enter contact email"
            nameFieldText="Enter contact full name"
            addButtonText="Add customer contact"
            contactFieldName="primary-customer-contact"
            isAddNewContactDisabled
            isExistingContactsBeEditable
            isExistingContactRemoveDisabled
            validateEmailField={
              isCustomerContactsEditable
                ? (email) => {
                    return validatePrimaryEmailDomain(email, validDomains);
                  }
                : undefined
            }
          />
        )}
      </Field>
    );
  };

  const renderSecondaryContactField = () => {
    if (shouldRenderForInternalUse) {
      const selectedOtherContactsBillingAdmins = (billingProfileData as any)?.['secondary-customer-contact-select'];

      return (
        <Field label="Other contacts" name="secondary-customer-contact" isRequired={false}>
          {() => (
            <AddContactMultiSelectField
              currentUserList={existingBillingAdminIds}
              emailFieldText="Select from the list"
              fieldType="secondary-customer-contact"
              billingAdmins={txaBillingAdmins.map((admin) => admin.node)}
              billingAdminsLoading={billingAdminsLoading}
              setBillingAdmins={setOtherContacts}
              selectedUserList={selectedOtherContactsBillingAdmins ?? []}
            />
          )}
        </Field>
      );
    }

    return (
      <Field label="Enter a secondary customer contact(s)" name={'secondary-customer-contact'} isRequired={false}>
        {() => (
          <AddContactFields
            setAtlassianAccountIds={setSecondaryCustomerContactAccountIds}
            atlassianAccountIds={secondaryCustomerContactAccountIds}
            atlassianAccountEmails={secondaryCustomerContactAccountEmails}
            setAtlassianAccountEmails={setSecondaryCustomerContactAccountEmails}
            userAlreadyExistText={
              shouldRenderForInternalUse ? 'Contact already exists' : 'Customer contact already exists'
            }
            emailFieldText="Enter contact email"
            nameFieldText="Enter contact full name"
            addButtonText={shouldRenderForInternalUse ? 'Add more contact' : 'Add secondary customer contact'}
            contactFieldName="secondary-customer-contact"
            isAddNewContactDisabled={isAddNewSecondaryContactDisabled}
            isContactMandatory={false}
          />
        )}
      </Field>
    );
  };

  return (
    <FormSection>
      <Box id="customer-details-step">
        {renderFormHeader()}
        {!isSoldToAddressAdded && renderSoldToAddressField()}
        {isAddressSelected && (
          <>
            {isMultiCurrencyChangesEnabled && showCurrencyWarning && (
              <div className="country-message-wrapper">
                <EditorInfoIcon label="" size="medium" primaryColor={token('color.icon', P200)} />
                <div className="country-message">
                  {`Currency will be set to ${getCurrencyByCountry(
                    selectedSoldToAddress?.country
                  )} for this sold to address.`}
                </div>
              </div>
            )}
          </>
        )}

        <Box id="customer-contact-section">
          <CustomerContactSectionStyles>
            {renderPrimaryContactField()}
            {renderSecondaryContactField()}
          </CustomerContactSectionStyles>
        </Box>
      </Box>
    </FormSection>
  );
};
